import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { authService } from '../services/auth.service'
import { User, LoginRequest, LoginResponse, AuthState } from '../types'
import { tokenUtils, AUTH_EXPIRED_EVENT, hasPermission as matchPermission } from '../utils/auth.utils'

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginRequest) => Promise<LoginResponse>
  logout: () => Promise<void>
  updateUser: (updates: Partial<User>) => void
  hasPermission: (permission: string) => boolean
  hasRole: (roleName: string) => boolean
}

const LOGGED_OUT: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Core auth state machine. Used by `AuthProvider`, and as a standalone fallback
 * by `useAuth` when no provider is mounted (so existing add-ins keep working).
 */
const useAuthState = (enabled: boolean): AuthContextValue => {
  const [state, setState] = useState<AuthState>({
    ...LOGGED_OUT,
    isLoading: enabled,
  })

  // Initialize auth state from stored tokens
  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    const initAuth = async () => {
      try {
        const tokens = tokenUtils.getTokens()
        if (tokens && !tokenUtils.isTokenExpired(tokens.expiresIn)) {
          const user = await authService.getCurrentUser()
          if (cancelled) return
          setState({
            user,
            tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } else {
          if (tokens) tokenUtils.clearTokens()
          if (!cancelled) setState(prev => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        if (cancelled) return
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to initialize auth',
        }))
      }
    }

    initAuth()
    return () => {
      cancelled = true
    }
  }, [enabled])

  // Drop to the login screen when the API client gives up refreshing the session
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const onExpired = () => setState({ ...LOGGED_OUT, error: 'Your session has expired. Please sign in again.' })
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired)
  }, [enabled])

  const login = useCallback(async (credentials: LoginRequest) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await authService.login(credentials)
      setState({
        user: response.user,
        tokens: response.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
      return response
    } catch (error) {
      const errorMessage =
        (error as { message?: string })?.message || 'Login failed. Check your email and password.'
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }))

    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local state even if server logout fails
      setState({ ...LOGGED_OUT })
    }
  }, [])

  const updateUser = useCallback((updates: Partial<User>) => {
    setState(prev => ({
      ...prev,
      user: prev.user ? { ...prev.user, ...updates } : null,
    }))
  }, [])

  const hasPermission = useCallback((permission: string): boolean => {
    if (!state.user) return false
    if (matchPermission(state.user.permissions ?? [], permission)) return true
    return state.user.roles.some(role =>
      role.permissions.some(p => p.name === permission)
    )
  }, [state.user])

  const hasRole = useCallback((roleName: string): boolean => {
    if (!state.user) return false
    return state.user.roles.some(role => role.name === roleName)
  }, [state.user])

  return useMemo(
    () => ({ ...state, login, logout, updateUser, hasPermission, hasRole }),
    [state, login, logout, updateUser, hasPermission, hasRole]
  )
}

/**
 * Shares one auth session across the whole add-in tree. Mount it once at the
 * entry point so the login form and the task pane observe the same state.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useAuthState(true)
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Access the auth session. Uses the nearest `AuthProvider`; without one it
 * falls back to a component-local session (legacy behaviour).
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  const fallback = useAuthState(context === null)
  return context ?? fallback
}
